import { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, Download, CalendarDays, Users, FileText, DollarSign } from 'lucide-react';
import { CaseDrawer } from './CaseDrawer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_STYLE = {
  qualified: 'bg-blue-50 text-blue-700 border-blue-200',
  out_of_scope: 'bg-gray-100 text-gray-600 border-gray-200',
  signed: 'bg-amber-50 text-amber-700 border-amber-200',
  checkout_started: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  scheduled: 'bg-teal-50 text-teal-700 border-teal-200',
};

function useEtClock() {
  const [now, setNow] = useState('');
  useEffect(() => {
    const fmt = () => new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    }).format(new Date());
    setNow(fmt());
    const t = setInterval(() => setNow(fmt()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

const Stat = ({ label, value, sub, Icon, accent }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`hc-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
      {Icon && <Icon className={`w-4 h-4 ${accent || 'text-gray-400'}`} />}
    </div>
    <div className="mt-2 text-2xl font-semibold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

function BridgeHealth({ jget, post, bridgeConnected }) {
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => jget('/v1/admin/health-check/bridge-health').then(setHealth).catch(() => {}), [jget]);
  useEffect(() => { load(); }, [load]);

  const run = async (kind) => {
    if (kind === 'all' && !window.confirm('Re-push EVERY paid/scheduled engagement and every quote to app.kgob.com? Their consumer will re-import each record (deduped by id).')) return;
    setBusy(kind);
    try {
      const r = await post(kind === 'all' ? '/v1/admin/health-check/resync-all' : '/v1/admin/health-check/resync-dirty', {});
      window.alert(kind === 'all'
        ? `Re-pushed ${r.engagements_ok}/${r.engagements} engagement(s) and ${r.quotes_ok}/${r.quotes} quote(s).`
        : `Checked ${(r.engagements_checked || 0) + (r.quotes_checked || 0)} dirty record(s); resynced ${(r.engagements_resynced || 0) + (r.quotes_resynced || 0)}.`);
      await load();
    } finally { setBusy(''); }
  };

  const e = health?.engagements || {}; const q = health?.quotes || {};
  const dirty = (e.dirty || 0) + (q.dirty || 0);
  const never = (e.never_synced || 0) + (q.never_synced || 0);
  const ok = dirty === 0 && never === 0;
  const lastSync = e.last_synced_at || q.last_synced_at;
  return (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border px-4 py-3 ${ok ? 'bg-emerald-50/60 border-emerald-200' : 'bg-amber-50/70 border-amber-300'}`} data-testid="bridge-health-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Activity className={`w-4 h-4 ${ok ? 'text-emerald-600' : 'text-amber-600'}`} /> Bridge Data Health
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${bridgeConnected ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{bridgeConnected ? 'connected' : 'NOT CONNECTED'}</span>
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-600" data-testid="bridge-health-stats">
        <span>Dirty (failed writes): <strong className={dirty ? 'text-amber-700' : 'text-gray-900'}>{health ? dirty : '—'}</strong></span>
        <span>Never synced: <strong className={never ? 'text-amber-700' : 'text-gray-900'}>{health ? never : '—'}</strong></span>
        <span>Tracked: <strong className="text-gray-900">{health ? `${e.total ?? 0} eng · ${q.total ?? 0} quotes` : '—'}</strong></span>
        {lastSync && <span>Last sync: <strong className="text-gray-900">{new Date(lastSync).toLocaleString()}</strong></span>}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <button onClick={() => run('dirty')} disabled={!!busy} className="flex items-center gap-1.5 text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50" data-testid="bridge-health-resync-dirty">
          <RefreshCw className={`w-3.5 h-3.5 ${busy === 'dirty' ? 'animate-spin' : ''}`} /> Resync dirty
        </button>
        <button onClick={() => run('all')} disabled={!!busy} className="flex items-center gap-1.5 text-xs bg-[#0B2240] text-white rounded-lg px-2.5 py-1.5 hover:opacity-90 disabled:opacity-50" data-testid="bridge-health-resync-all">
          <RefreshCw className={`w-3.5 h-3.5 ${busy === 'all' ? 'animate-spin' : ''}`} /> Resync ALL to app.kgob.com
        </button>
      </div>
    </div>
  );
}

export default function HealthCheckTab({ sessionToken }) {
  const [view, setView] = useState('engagements');
  const [overview, setOverview] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [appts, setAppts] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [slots, setSlots] = useState([]);
  const [payments, setPayments] = useState([]);
  const [billing, setBilling] = useState(null);
  const [refunding, setRefunding] = useState('');
  const [caseId, setCaseId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [newBlock, setNewBlock] = useState('');
  const etNow = useEtClock();

  const H = { Authorization: `Bearer ${sessionToken}` };
  const jget = useCallback((p) => fetch(`${API}${p}`, { headers: H }).then((r) => r.json()), [sessionToken]); // eslint-disable-line

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, en, qu, ap, av, bl] = await Promise.all([
        jget('/v1/admin/health-check/overview'),
        jget('/v1/admin/health-check/engagements?limit=300'),
        jget('/v1/admin/health-check/quotes'),
        jget('/v1/admin/health-check/appointments'),
        fetch(`${API}/v1/health-check/schedule/availability`).then((r) => r.json()),
        jget('/v1/admin/billing/payments?limit=300'),
      ]);
      setOverview(ov);
      setEngagements(en.engagements || []);
      setQuotes(qu.quotes || []);
      setAppts(ap.appointments || []);
      setBlocked(av.blocked_dates || []);
      setSlots(av.slots || []);
      setPayments(bl.payments || []);
      setBilling(bl.summary || null);
    } finally {
      setLoading(false);
    }
  }, [jget]);

  useEffect(() => { if (sessionToken) loadAll(); }, [sessionToken, loadAll]);

  const post = (p, body) => fetch(`${API}${p}`, { method: 'POST', headers: { ...H, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());

  const syncFromApp = async () => { setSyncing(true); try { await post('/v1/admin/health-check/sync-from-app', {}); await loadAll(); } finally { setSyncing(false); } };
  const openManage = async (e) => {
    const r = await post('/v1/admin/health-check/manage-token', { engagement_id: e.engagement_id });
    if (r.manage_token) { sessionStorage.setItem('bhc_manage_token', r.manage_token); window.open('/business-health-check/manage', '_blank'); }
  };
  const exportCsv = () => fetch(`${API}/v1/admin/health-check/export`, { headers: H }).then((r) => r.blob()).then((b) => { const u = URL.createObjectURL(b); const a = document.createElement('a'); a.href = u; a.download = 'health_check_engagements.csv'; a.click(); URL.revokeObjectURL(u); });

  const assign = async (bk) => {
    const name = window.prompt('Assign team member to this intake call:', bk.assigned_to || '');
    if (name == null) return;
    await post('/v1/admin/health-check/assign', { booking_id: bk.booking_id, assigned_to: name });
    loadAll();
  };
  const reschedule = async (bk, date, slot) => {
    if (!date || !slot) return;
    const d = new Date(date + 'T00:00:00');
    const label = `${d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })} at ${slot} ET`;
    await post('/v1/admin/health-check/reschedule', { booking_id: bk.booking_id, date, slot, label });
    loadAll();
  };
  const addBlock = async () => { if (!newBlock) return; await post('/v1/admin/health-check/blocked-dates', { date: newBlock, reason: 'Blocked by admin' }); setNewBlock(''); loadAll(); };
  const removeBlock = async (d) => { await fetch(`${API}/v1/admin/health-check/blocked-dates/${d}`, { method: 'DELETE', headers: H }); loadAll(); };

  const refund = async (p) => {
    if (!window.confirm(`Refund ${p.amount_display || '$4,995.00'} to ${p.client_name || p.email || 'this client'}? This issues a real Stripe refund.`)) return;
    const reason = window.prompt('Refund reason (optional):', '') ?? '';
    setRefunding(p.session_id);
    try {
      const r = await post('/v1/admin/billing/refund', { session_id: p.session_id, reason });
      if (r.refund_status === 'refunded') {
        window.alert(r.already || r.synced ? 'Already refunded — ledger synced.' : 'Refund issued.');
      } else {
        window.alert(r.detail || 'Refund could not be completed.');
      }
      await loadAll();
    } finally { setRefunding(''); }
  };
  const reconcileRefunds = async () => {
    setReconciling(true);
    try { const r = await post('/v1/admin/billing/reconcile-refunds', {}); window.alert(`Checked ${r.checked} sale(s); ${r.synced} refund(s) synced from Stripe.`); await loadAll(); }
    finally { setReconciling(false); }
  };

  const f = overview?.funnel || {};
  const bridgeConnected = overview?.bridge?.connected;

  return (
    <div className="space-y-5" data-testid="health-check-tab">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Business Health Check</h2>
          <p className="text-sm text-gray-500">Premium paid funnel · $4,995 · synced with app.kgob.com</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm bg-gray-900 text-white px-3 py-1.5 rounded-lg font-mono" data-testid="et-clock" title="app.kgob.com runs on Eastern Time">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> {etNow} ET
          </div>
          <button onClick={syncFromApp} disabled={syncing} className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid="hc-sync-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : 'Sync from app.kgob.com'}
          </button>
          <button onClick={exportCsv} className="flex items-center gap-2 text-sm bg-[#0B2240] text-white rounded-lg px-3 py-1.5 hover:opacity-90" data-testid="hc-export-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* bridge data health — dirty/stale delivery counts + one-click resync */}
      <BridgeHealth jget={jget} post={post} bridgeConnected={bridgeConnected} />

      {/* metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Qualified" value={f.qualified ?? '—'} Icon={Users} accent="text-blue-500" />
        <Stat label="Signed" value={f.signed ?? '—'} Icon={FileText} accent="text-amber-500" />
        <Stat label="Paid" value={f.paid ?? '—'} Icon={DollarSign} accent="text-emerald-500" />
        <Stat label="Scheduled" value={f.scheduled ?? '—'} Icon={CalendarDays} accent="text-teal-500" />
        <Stat label="Conversion" value={overview ? `${overview.conversion_rate}%` : '—'} sub="qualified → paid" />
        <Stat label="Revenue" value={overview ? `$${(overview.revenue || 0).toLocaleString()}` : '—'} sub={`${f.paid || 0} paid`} accent="text-emerald-500" />
      </div>

      {/* sub-nav */}
      <div className="flex gap-2 border-b border-gray-200">
        {[['engagements', `Engagements (${engagements.length})`], ['quotes', `Custom Quotes (${quotes.length})`], ['calendar', `Calendar (${appts.length})`], ['billing', `Billing (${payments.length})`]].map(([k, lbl]) => (
          <button key={k} onClick={() => setView(k)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${view === k ? 'border-[#C29B2E] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`hc-subtab-${k}`}>{lbl}</button>
        ))}
      </div>

      {loading ? <div className="text-sm text-gray-400 py-10 text-center">Loading…</div> : (
        <>
          {view === 'engagements' && (
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm" data-testid="hc-engagements-table">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  {['Name', 'Contact', 'Type', 'Stage', 'Paid', 'Intake call', 'Assigned', 'Notes'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {engagements.map((e) => (
                    <tr key={e.engagement_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-medium text-gray-900">{e.contact?.name || '—'}<div className="text-xs text-gray-400">{e.contact?.business}</div>{e.engagement_ref && <div className="text-[11px] font-mono text-[#C29B2E]" data-testid={`hc-ref-${e.engagement_id}`}>{e.engagement_ref}</div>}{(e.status === 'paid' || e.status === 'scheduled') && e.pii_status && (e.pii_status.complete ? <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`hc-pii-${e.engagement_id}`}>PII complete</span> : <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title={`Missing: ${e.pii_status.missing.join(', ')}`} data-testid={`hc-pii-${e.engagement_id}`}>PII: {e.pii_status.missing_count} missing</span>)}</td>
                      <td className="px-3 py-2.5 text-gray-600">{e.contact?.email}<div className="text-xs text-gray-400">{e.contact?.phone}</div></td>
                      <td className="px-3 py-2.5 capitalize">{e.variant}</td>
                      <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${STAGE_STYLE[e.status] || 'bg-gray-100 text-gray-600'}`}>{e.status}</span></td>
                      <td className="px-3 py-2.5">{e.payment?.amount_display || (e.status === 'paid' || e.status === 'scheduled' ? '$4,995.00' : '—')}</td>
                      <td className="px-3 py-2.5 text-gray-600">{e.booking?.label || '—'}{e.booking?.booking_ref && <div className="text-xs text-gray-400">{e.booking.booking_ref}</div>}</td>
                      <td className="px-3 py-2.5 text-gray-600">{e.booking?.assigned_to || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600 max-w-xs">
                        <div className="flex items-center gap-2">
                          {e.client_notes ? <span className="text-xs text-gray-700 line-clamp-2" title={e.client_notes} data-testid={`hc-notes-${e.engagement_id}`}>{e.client_notes}</span> : <span className="text-xs text-gray-300">—</span>}
                          {(e.status === 'paid' || e.status === 'scheduled') && (
                            <>
                              <button onClick={() => setCaseId(e.engagement_id)} className="shrink-0 text-xs border border-[#C29B2E]/50 text-[#8F7218] rounded px-2 py-0.5 hover:bg-[#C29B2E]/10" data-testid={`hc-casefile-${e.engagement_id}`} title="Open the full case file — documents, messages, tickets, business details">Case file</button>
                              <button onClick={() => openManage(e)} className="shrink-0 text-xs border border-[#0B2240]/30 text-[#0B2240] rounded px-2 py-0.5 hover:bg-[#0B2240]/5" data-testid={`hc-manage-${e.engagement_id}`} title="Open the client's self-service page (e.g. to reschedule together on a call)">Manage</button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!engagements.length && <tr><td colSpan={8} className="text-center text-gray-400 py-8">No engagements yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {view === 'quotes' && (
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm" data-testid="hc-quotes-table">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                  {['Name', 'Contact', 'Areas', 'Timeline', 'Detail'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {quotes.map((q) => (
                    <tr key={q.quote_id} className="hover:bg-gray-50 align-top">
                      <td className="px-3 py-2.5 font-medium text-gray-900">{q.contact?.name}<div className="text-xs text-gray-400">{q.contact?.business}</div></td>
                      <td className="px-3 py-2.5 text-gray-600">{q.contact?.email}<div className="text-xs text-gray-400">{q.contact?.phone}</div></td>
                      <td className="px-3 py-2.5 text-gray-600 max-w-xs">{(q.areas || []).join(', ') || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-600">{q.timeline || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-500 max-w-sm truncate" title={q.detail}>{q.detail || '—'}</td>
                    </tr>
                  ))}
                  {!quotes.length && <tr><td colSpan={5} className="text-center text-gray-400 py-8">No custom-quote requests yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {view === 'billing' && (
            <div className="space-y-4" data-testid="hc-billing">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="text-gray-500">Revenue (net): <span className="font-semibold text-gray-900">${((billing?.revenue || 0)).toLocaleString()}</span></span>
                  <span className="text-emerald-700">Paid: {billing?.paid ?? 0}</span>
                  <span className="text-rose-700">Refunded: {billing?.refunded ?? 0}</span>
                  <span className="text-amber-700">Pending: {billing?.pending ?? 0}</span>
                </div>
                <button onClick={reconcileRefunds} disabled={reconciling} className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid="hc-reconcile-refunds-btn" title="Pull refund state from Stripe — catches refunds issued on app.kgob.com or the Stripe Dashboard">
                  <RefreshCw className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} /> {reconciling ? 'Syncing…' : 'Sync refunds from Stripe'}
                </button>
              </div>
              <p className="text-xs text-gray-400">Shared Stripe account with app.kgob.com. Refunds issued here or there stay in sync — Stripe is the source of truth.</p>
              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full text-sm" data-testid="hc-billing-table">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                    {['Client', 'Amount', 'Status', 'Card', 'Paid at', 'Refund', 'Action'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {payments.map((p) => {
                      const refunded = p.payment_status === 'refunded' || p.refund_status === 'refunded';
                      const paid = p.payment_status === 'paid';
                      return (
                        <tr key={p.session_id} className="hover:bg-gray-50 align-top" data-testid={`hc-billing-row-${p.session_id}`}>
                          <td className="px-3 py-2.5 font-medium text-gray-900">{p.client_name || '—'}<div className="text-xs text-gray-400">{p.email}{p.business ? ` · ${p.business}` : ''}</div></td>
                          <td className="px-3 py-2.5">{p.amount_display || (p.amount ? `$${Number(p.amount).toLocaleString()}` : '—')}</td>
                          <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${refunded ? 'bg-rose-50 text-rose-700 border-rose-200' : paid ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{refunded ? 'refunded' : p.payment_status}</span></td>
                          <td className="px-3 py-2.5 text-gray-600">{p.card_brand ? `${p.card_brand} ••${p.card_last4 || '----'}` : <span className="text-gray-300">—</span>}</td>
                          <td className="px-3 py-2.5 text-xs text-gray-500">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}</td>
                          <td className="px-3 py-2.5 text-xs">{refunded ? <span className="text-rose-600" title={p.refunded_by || ''}>refunded{p.refunded_by ? ` · ${p.refunded_by}` : ''}</span> : <span className="text-gray-300">—</span>}</td>
                          <td className="px-3 py-2.5">
                            {paid && !refunded ? (
                              <button onClick={() => refund(p)} disabled={refunding === p.session_id} className="text-xs border border-rose-300 text-rose-700 rounded px-2 py-1 hover:bg-rose-50 disabled:opacity-50" data-testid={`hc-refund-${p.session_id}`}>{refunding === p.session_id ? 'Refunding…' : 'Refund'}</button>
                            ) : <span className="text-xs text-gray-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {!payments.length && <tr><td colSpan={7} className="text-center text-gray-400 py-8">No sales yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {view === 'calendar' && (
            <div className="space-y-5">
              {/* blocked dates */}
              <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="hc-blocked-manager">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-semibold text-gray-900 text-sm">Blocked dates / holidays <span className="font-normal text-gray-400">(syncs both ways with app.kgob.com)</span></h3>
                  <div className="flex items-center gap-2">
                    <input type="date" value={newBlock} onChange={(e) => setNewBlock(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1 text-sm" data-testid="hc-block-date-input" />
                    <button onClick={addBlock} className="text-sm bg-gray-900 text-white rounded-lg px-3 py-1.5" data-testid="hc-add-block">Block date</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {blocked.length ? blocked.map((d) => (
                    <span key={d} className="inline-flex items-center gap-2 text-xs bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-3 py-1">{d}<button onClick={() => removeBlock(d)} className="font-bold hover:text-rose-900" data-testid={`hc-unblock-${d}`}>×</button></span>
                  )) : <span className="text-sm text-gray-400">No blocked dates.</span>}
                </div>
              </div>

              {/* appointments */}
              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full text-sm" data-testid="hc-appointments-table">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr>
                    {['When (ET)', 'Status', 'Assigned to', 'Source', 'Actions'].map((h) => <th key={h} className="text-left font-semibold px-3 py-2.5">{h}</th>)}
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {appts.map((b) => <ApptRow key={b.booking_id} bk={b} slots={slots} onAssign={assign} onReschedule={reschedule} />)}
                    {!appts.length && <tr><td colSpan={5} className="text-center text-gray-400 py-8">No intake calls booked yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
      {caseId && <CaseDrawer engagementId={caseId} H={H} onClose={() => { setCaseId(null); loadAll(); }} />}
    </div>
  );
}

function ApptRow({ bk, slots, onAssign, onReschedule }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(bk.date || '');
  const [slot, setSlot] = useState(bk.slot || '');
  return (
    <tr className="hover:bg-gray-50 align-top" data-testid={`hc-appt-${bk.booking_id}`}>
      <td className="px-3 py-2.5 font-medium text-gray-900">{bk.label}</td>
      <td className="px-3 py-2.5"><span className="text-xs px-2 py-0.5 rounded-full border bg-teal-50 text-teal-700 border-teal-200">{bk.status}</span></td>
      <td className="px-3 py-2.5 text-gray-600">{bk.assigned_to || <span className="text-amber-600">Unassigned</span>}</td>
      <td className="px-3 py-2.5 text-xs text-gray-400">{bk.synced_from || 'kgob.com'}</td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <button onClick={() => onAssign(bk)} className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50" data-testid={`hc-assign-${bk.booking_id}`}>Assign</button>
          <button onClick={() => setOpen((v) => !v)} className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50" data-testid={`hc-reschedule-toggle-${bk.booking_id}`}>Reschedule</button>
        </div>
        {open && (
          <div className="flex items-center gap-2 mt-2">
            <input data-testid="health-check-tab-input-1" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-xs" />
            <select data-testid="health-check-tab-select-1" value={slot} onChange={(e) => setSlot(e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-xs">
              <option value="">Slot…</option>{(slots || []).map((s) => <option key={s}>{s}</option>)}
            </select>
            <button onClick={() => { onReschedule(bk, date, slot); setOpen(false); }} className="text-xs bg-gray-900 text-white rounded px-2 py-1" data-testid={`hc-reschedule-save-${bk.booking_id}`}>Save</button>
          </div>
        )}
      </td>
    </tr>
  );
}
